function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

async function getPdfLib() {
  if (window.PDFLib) {
    return window.PDFLib;
  }
  await loadScript('https://unpkg.com/pdf-lib@1.17.1/dist/pdf-lib.min.js');
  if (!window.PDFLib) {
    throw new Error('pdf-lib non disponibile');
  }
  return window.PDFLib;
}

async function getPdfJs() {
  if (!window.pdfjsLib) {
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js');
    window.pdfjsLib.GlobalWorkerOptions.workerSrc =
      'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  }
  return window.pdfjsLib;
}

async function fetchPdfBytes(pdfUrl) {
  const response = await fetch(pdfUrl);
  if (!response.ok) {
    throw new Error(`Impossibile scaricare il contratto (${response.status})`);
  }
  return response.arrayBuffer();
}

/** Fallback se il testo nel PDF non è leggibile. */
export function getSignaturePageIndex(pageCount) {
  if (pageCount <= 1) return 0;
  if (pageCount >= 3) return pageCount - 2;
  return 1;
}

function fallbackPlacement(pageSize) {
  return {
    x: pageSize.width * 0.55,
    y: pageSize.height * 0.58,
    width: 180,
    height: 42,
  };
}

function buildPlacementFromLabel(item, pageWidth) {
  const x = item.transform[4];
  const y = item.transform[5];
  const text = item.str || '';
  const prefixWidth = /Firma:/i.test(text) ? 40 : 62;
  return {
    x: x + prefixWidth,
    y: Math.max(24, y - 4),
    width: Math.min(200, pageWidth - (x + prefixWidth) - 20),
    height: 42,
  };
}

/**
 * Trova pagina e coordinate della riga "Signature:" / "Firma:" dell'Agente (colonna destra).
 */
async function resolveAgentSignaturePlacement(pdf) {
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();
    const viewport = page.getViewport({ scale: 1 });
    const pageWidth = viewport.width;
    const pageHeight = viewport.height;

    const hasAgentSection = textContent.items.some((item) =>
      /For the Agent|Per l'Agente/i.test(item.str || '')
    );
    if (!hasAgentSection) continue;

    let agentLabel = null;
    for (const item of textContent.items) {
      const text = item.str || '';
      if (!/(Signature:|Firma:)/i.test(text)) continue;
      const x = item.transform[4];
      if (x < pageWidth * 0.35) continue;
      if (!agentLabel || x > agentLabel.transform[4]) {
        agentLabel = item;
      }
    }

    if (agentLabel) {
      return {
        pageIndex: pageNum - 1,
        placement: buildPlacementFromLabel(agentLabel, pageWidth),
        pageSize: { width: pageWidth, height: pageHeight },
      };
    }
  }

  const pageIndex = getSignaturePageIndex(pdf.numPages);
  const page = await pdf.getPage(pageIndex + 1);
  const viewport = page.getViewport({ scale: 1 });
  return {
    pageIndex,
    placement: fallbackPlacement({
      width: viewport.width,
      height: viewport.height,
    }),
    pageSize: { width: viewport.width, height: viewport.height },
  };
}

async function uint8ArrayToBase64(bytes) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = String(dataUrl).split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Errore conversione PDF'));
    reader.readAsDataURL(blob);
  });
}

function drawPlacementHighlight(context, placement, scale, label) {
  const boxX = placement.x * scale;
  const boxW = placement.width * scale;
  const boxH = placement.height * scale;
  const boxY = context.canvas.height - (placement.y + placement.height) * scale;

  context.save();
  context.fillStyle = 'rgba(229, 62, 62, 0.18)';
  context.fillRect(boxX, boxY, boxW, boxH);
  context.strokeStyle = '#e53e3e';
  context.lineWidth = 2;
  context.setLineDash([5, 4]);
  context.strokeRect(boxX, boxY, boxW, boxH);
  context.setLineDash([]);
  context.fillStyle = '#c53030';
  context.font = `bold ${Math.max(11, 12 * scale)}px Arial, sans-serif`;
  context.fillText(label, boxX, Math.max(14, boxY - 6));
  context.restore();
}

export async function getContractPageCount(pdfUrl) {
  const { PDFDocument } = await getPdfLib();
  const pdfDoc = await PDFDocument.load(await fetchPdfBytes(pdfUrl));
  return pdfDoc.getPageCount();
}

/**
 * Disegna la pagina firme e evidenzia la riga Firma dell'Agente.
 */
export async function renderContractSignaturePreview(pdfUrl, canvas, highlightLabel) {
  const pdfjsLib = await getPdfJs();
  const bytes = await fetchPdfBytes(pdfUrl);
  const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
  const resolved = await resolveAgentSignaturePlacement(pdf);
  const page = await pdf.getPage(resolved.pageIndex + 1);

  const baseViewport = page.getViewport({ scale: 1 });
  const containerWidth = canvas.parentElement?.clientWidth || 640;
  const scale = containerWidth / baseViewport.width;
  const viewport = page.getViewport({ scale });

  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);

  const context = canvas.getContext('2d');
  await page.render({ canvasContext: context, viewport }).promise;
  drawPlacementHighlight(context, resolved.placement, scale, highlightLabel);

  return {
    pageIndex: resolved.pageIndex,
    pageCount: pdf.numPages,
    placement: resolved.placement,
  };
}

/**
 * Incolla la firma sulla riga "Firma:" / "Signature:" dell'Agente.
 */
export async function mergeSignatureIntoPdf(pdfUrl, signaturePngDataUrl) {
  const pdfjsLib = await getPdfJs();
  const bytes = await fetchPdfBytes(pdfUrl);
  // pdf.js svuota l'ArrayBuffer: servono due copie separate
  const bytesForPdfJs = bytes.slice(0);
  const bytesForPdfLib = bytes.slice(0);

  const pdfjsDoc = await pdfjsLib.getDocument({ data: bytesForPdfJs }).promise;
  const resolved = await resolveAgentSignaturePlacement(pdfjsDoc);

  const { PDFDocument } = await getPdfLib();
  const pdfDoc = await PDFDocument.load(bytesForPdfLib);
  const pngImage = await pdfDoc.embedPng(signaturePngDataUrl);
  const targetPage = pdfDoc.getPages()[resolved.pageIndex];

  targetPage.drawImage(pngImage, resolved.placement);

  const signedBytes = await pdfDoc.save({ useObjectStreams: false });
  return signedBytes;
}
